import React, { useMemo, useState } from "react";
import { AlertCircle, Check, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@cms/ui/components/dialog";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { BookingModuleGuard, BookingModuleHeader } from "../components/booking-module-shell";
import {
  addDaysUtc,
  bookingItemLabel,
  dayKeyInZone,
  fetchBookings,
  fetchBookingSettings,
  formatDayHeading,
  formatInstant,
  formatTimeInZone,
  startOfWeekUtc,
  updateBookingStatus,
  type PortalBooking,
} from "../lib/booking";
import type { ClientOutletContext } from "../lib/outlet-context";

const STATUS_BADGE: Record<string, "success" | "secondary" | "destructive"> = {
  confirmed: "success",
  pending: "secondary",
  cancelled: "destructive",
};

function Calendar({ api }: ClientOutletContext) {
  const [weekStart, setWeekStart] = useState(() => startOfWeekUtc(new Date()));
  const weekEnd = useMemo(() => addDaysUtc(weekStart, 7), [weekStart]);

  const settingsQuery = useApiQuery(() => (api ? fetchBookingSettings(api) : null), [api]);
  const timezone = settingsQuery.data?.settings.timezone ?? "Europe/London";

  const query = useApiQuery(
    () => (api ? fetchBookings(api, weekStart.toISOString(), weekEnd.toISOString()) : null),
    [api, weekStart]
  );
  const bookings = query.data?.bookings ?? null;

  const [selected, setSelected] = useState<PortalBooking | null>(null);
  const [updating, setUpdating] = useState<"confirmed" | "cancelled" | null>(null);
  const [error, setError] = useState("");

  const byDay = useMemo(() => {
    const map = new Map<string, PortalBooking[]>();
    for (const booking of bookings ?? []) {
      const key = dayKeyInZone(booking.starts_at, timezone);
      map.set(key, [...(map.get(key) ?? []), booking]);
    }
    return map;
  }, [bookings, timezone]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysUtc(weekStart, i)),
    [weekStart]
  );

  const setStatus = async (status: "confirmed" | "cancelled") => {
    if (!api || !selected) return;
    setUpdating(status);
    setError("");
    try {
      await updateBookingStatus(api, selected.id, status);
      setSelected(null);
      query.reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <BookingModuleHeader
        action={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addDaysUtc(weekStart, -7))} aria-label="Previous week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeekUtc(new Date()))}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addDaysUtc(weekStart, 7))} aria-label="Next week">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      {!bookings && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {bookings && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {days.map((day) => {
                  const key = day.toISOString().slice(0, 10);
                  const dayBookings = byDay.get(key) ?? [];
                  return (
                    <tr key={key} className="border-b align-top last:border-0">
                      <td className="w-36 whitespace-nowrap px-4 py-3 font-medium text-muted-foreground">
                        {formatDayHeading(day, timezone)}
                      </td>
                      <td className="px-4 py-2">
                        {dayBookings.length === 0 && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {dayBookings.map((booking) => (
                            <button
                              key={booking.id}
                              type="button"
                              onClick={() => { setError(""); setSelected(booking); }}
                              className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left hover:bg-muted/50"
                            >
                              <span className="font-medium tabular-nums">
                                {formatTimeInZone(booking.starts_at, timezone)}
                              </span>
                              <span>{booking.name}</span>
                              {booking.party_size ? (
                                <span className="text-xs text-muted-foreground">×{booking.party_size}</span>
                              ) : null}
                              <Badge variant={STATUS_BADGE[booking.status] ?? "secondary"}>
                                {booking.status}
                              </Badge>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(open: boolean) => !open && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
            <DialogDescription>
              {selected && `${formatInstant(selected.starts_at, timezone)} · ${bookingItemLabel(selected)}`}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Email:</span> {selected.email}</p>
              {selected.phone && <p><span className="text-muted-foreground">Phone:</span> {selected.phone}</p>}
              {selected.party_size != null && (
                <p><span className="text-muted-foreground">Party size:</span> {selected.party_size}</p>
              )}
              {selected.notes && <p><span className="text-muted-foreground">Notes:</span> {selected.notes}</p>}
              <p>
                <span className="text-muted-foreground">Status:</span>{" "}
                <Badge variant={STATUS_BADGE[selected.status] ?? "secondary"}>{selected.status}</Badge>
              </p>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          )}
          <DialogFooter>
            {selected?.status !== "cancelled" && (
              <Button
                variant="outline"
                className="text-destructive"
                disabled={updating !== null}
                onClick={() => { void setStatus("cancelled"); }}
              >
                {updating === "cancelled" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Cancel booking
              </Button>
            )}
            {selected?.status === "pending" && (
              <Button disabled={updating !== null} onClick={() => { void setStatus("confirmed"); }}>
                {updating === "confirmed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Confirm booking
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BookingCalendarPage() {
  return (
    <BookingModuleGuard
      title="Bookings"
      description="This week's bookings — click one to confirm or cancel it."
    >
      {(ctx) => <Calendar {...ctx} />}
    </BookingModuleGuard>
  );
}
